import { ExclamationCircleFilled } from "@ant-design/icons";
import { Box } from "@chakra-ui/react";
import { Button, List, Modal, Typography } from "antd";
import { useState } from "react";
import { useAccountDeletion } from "./AccountDeletion";

const { Paragraph, Text } = Typography;

const DeleteAccount = ({ onConfirm }: { deletionDate?: string | null, onConfirm: () => Promise<void> }) => {
    const [open, setOpen] = useState(false);
    const {
        preFlightCheck: {
            data: preFlightData,
            isLoading: isCheckingStatus,
        },
        enqueueDeletion: {
            mutateAsync: deleteAccount,
            isLoading: isEnqueuingDelete,
            reset,
            error,
        },
        isBreaker
    } = useAccountDeletion(open);

    const close = () => {
        reset();
        setOpen(false);
    }

    const enqueueDeletion = async () => {
        await deleteAccount();
        await onConfirm();
        close();
        Modal.info({
            title: "Account Deletion Enqueued",
            content: (<Box>
                {isBreaker
                    ? "Your account has been scheduled for deletion. A dcsports87 administrator will be in touch with you shortly."
                    : "Your account has been scheduled for deletion. You may cancel your request before the grace period ends."}
            </Box>)
        });
    }

    const working = isCheckingStatus || isEnqueuingDelete;

    return <>
        <Button
            type="link"
            style={{ alignSelf: "end" }}
            onClick={() => setOpen(true)}
        >
            Delete Account
        </Button>
        <Modal
            open={open}
            confirmLoading={working}
            title="Delete Your dcsports87 Account"
            closable={false}
            keyboard={true}
            maskClosable={true}
            destroyOnClose={true}
            onCancel={close}
            onOk={enqueueDeletion}
            cancelButtonProps={{ disabled: working }}
            okButtonProps={{ disabled: working || !preFlightData?.ok }}
            okText={preFlightData?.ok ? "Delete My Account" : "OK"}
        >
            {isBreaker ? (<>
                <Paragraph>
                    As a breaker with us, requesting an account deletion will start a manual process, in which a dcsports87 administrator will contact you regarding how to
                    proceed.
                </Paragraph>
                <Paragraph>
                    As a placeholder, your account will set to be deleted 1 year from now, as the actual deletion date depends on how long
                    it takes to wind your account down. You may cancel your deletion request at any time.
                </Paragraph>
            </>
            ) : (
                <Paragraph>
                    Deleting your account will wipe all personal data, including ACH accounts with Chase, from dcsports87 after a grace period of 72 hours.
                    You may cancel your deletion request before that period ends.
                </Paragraph>
            )}

            {!!error && <Paragraph>
                <Text type="danger" strong>An error occurred while processing your account deletion request.</Text>
            </Paragraph>}

            {!working && !preFlightData?.ok && <>
                <Paragraph strong>
                    In order to proceed, you must have an empty account balance and no packages / cards currently being processed by us or eBay.
                </Paragraph>
                <Paragraph>
                    We are unable to accept a deletion request from you at this time due to the following:
                </Paragraph>
                <List
                    size="small"
                    bordered
                    dataSource={preFlightData?.reasons ?? []}
                    renderItem={item => <List.Item>
                        <ExclamationCircleFilled style={{ color: 'var(--dcs-error-red)', paddingRight: '10px' }} />
                        <Text strong style={{ flex: 1 }}>{item}</Text>
                    </List.Item>}
                />
                <Paragraph style={{ marginTop: '10px' }}>
                    If you feel that any of the above items are in error, please contact customer service.
                </Paragraph>
            </>}
        </Modal >
    </>;
}

export default DeleteAccount;